import { useEffect, useMemo, useState } from 'react';
import { useCanvasStore } from '../store/canvas-store';
import { loadAssetJson } from './loadAssetJson';
import { listSlots } from './resolveSlots';
import { lottieElementsOf } from './scan';
import type { LottieJson, LottieSlotInfo, LottieSlotValue } from './types';

const rgbaToHex = (c: number[]): string =>
  '#' + c.slice(0, 3).map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('');
const hexToRgba = (hex: string): number[] => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).concat(1);

/** Slot controls for one [data-lottie] element — the reference player's
 *  properties panel, relocated into the Editor Panel. */
export function LottieControls({ frameId, cfNodeId }: { frameId: string; cfNodeId: string }) {
  const frames = useCanvasStore((s) => s.doc.frames);
  const dispatch = useCanvasStore((s) => s.dispatch);
  const frame = frames.find((f) => f.id === frameId);
  const html = frame && 'html' in frame ? (frame as { html: string }).html : '';
  const spec = useMemo(() => lottieElementsOf(html).find((e) => e.cfNodeId === cfNodeId), [html, cfNodeId]);
  const [slots, setSlots] = useState<LottieSlotInfo[] | null>(null);

  useEffect(() => {
    if (!spec) return;
    void loadAssetJson(spec.ref).then((j) => setSlots(j ? listSlots(j as LottieJson) : []));
  }, [spec?.ref]);

  if (!spec) return null;
  const overrides = spec.slots;
  const set = (sid: string, value: LottieSlotValue) =>
    dispatch({ t: 'lottie.setSlots', frameId, cfNodeId, slots: { ...overrides, [sid]: value } });

  return (
    <div className="flex flex-col gap-2 p-2 font-[family-name:var(--font-mono)] text-xs">
      {(slots ?? []).map((s) => {
        const v = overrides[s.id] ?? s.value;
        return (
          <div key={s.id} className="flex items-center gap-2">
            <label htmlFor={`lottie-slot-${s.id}`} className="w-24 truncate text-[var(--color-text-muted)]">{s.id}</label>
            {s.type === 'scalar' && (
              <input id={`lottie-slot-${s.id}`} aria-label={s.id} type="range" min={0} max={Math.max(200, Number(v) * 2)} step={1}
                value={Number(v)} onChange={(e) => set(s.id, Number(e.target.value))} className="flex-1 accent-[var(--color-accent)]" />
            )}
            {/* v1: var(--color-*) token-bound colors show as #000000 and editing
                replaces the token with a literal rgba — full token-aware picker
                deferred. The color input only edits r,g,b; the slot's prior alpha
                is preserved so nudging the swatch never clobbers transparency. */}
            {s.type === 'color' && (
              <input id={`lottie-slot-${s.id}`} aria-label={s.id} type="color"
                value={typeof v === 'string' ? '#000000' : rgbaToHex(v as number[])}
                onChange={(e) => {
                  const rgb = hexToRgba(e.target.value);
                  const prevA = Array.isArray(v) ? (v[3] ?? 1) : 1;
                  set(s.id, [rgb[0], rgb[1], rgb[2], prevA]);
                }} />
            )}
            {s.type === 'vec2' && (
              <span className="flex gap-1">
                {[0, 1].map((axis) => (
                  <input key={axis} aria-label={axis === 0 ? s.id : `${s.id}-y`} id={axis === 0 ? `lottie-slot-${s.id}` : undefined}
                    type="number" className="w-16 bg-[var(--color-surface)] border border-[var(--color-chip-border)] rounded px-1"
                    value={(v as number[])[axis]}
                    onChange={(e) => { const next = [...(v as number[])]; next[axis] = Number(e.target.value); set(s.id, next); }} />
                ))}
              </span>
            )}
            {s.type === 'text' && (
              <input id={`lottie-slot-${s.id}`} aria-label={s.id} type="text" value={String(v)}
                onChange={(e) => set(s.id, e.target.value)}
                className="flex-1 bg-[var(--color-surface)] border border-[var(--color-chip-border)] rounded px-1" />
            )}
          </div>
        );
      })}
      {slots && slots.length === 0 && <div className="text-[var(--color-text-muted)]">No editable slots — ask the agent to expose some.</div>}
    </div>
  );
}
